import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import publicApi from '../helper/publicApi';
import geolocationService from '../services/geolocation.service';
import { useLocation as useLocationContext } from '../context/LocationContext';
import { useCart } from '../context/CartContext';

const SCROLL_THRESHOLD = 150;

const HeaderOne = () => {
  const headerRef = useRef(null);
  const middleHeaderRef = useRef(null);
  const tickingRef = useRef(false);
  const navigate = useNavigate();
  const { getCartItemsCount } = useCart();
  const cartIconRef = useRef(null);

  useEffect(() => {
    const handle = () => {
      const el = cartIconRef.current;
      if (!el) return;
      el.classList.remove('cart-bump');
      // restart animation
      void el.offsetWidth;
      el.classList.add('cart-bump');
      window.setTimeout(() => el.classList.remove('cart-bump'), 1100);

      // If user is scrolled down, peek the cart header briefly
      const header = middleHeaderRef.current;
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      const isDesktop = typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(min-width: 992px)').matches
        : true;

      if (header && isDesktop && scrollY > SCROLL_THRESHOLD) {
        header.classList.remove('header-middle-peek');
        void header.offsetWidth;
        header.classList.add('header-middle-peek');
        window.setTimeout(() => header.classList.remove('header-middle-peek'), 2000);
      }
    };
    window.addEventListener('delice:cart:add', handle);
    return () => window.removeEventListener('delice:cart:add', handle);
  }, []);

  // Handle Mon Compte click - redirect based on auth status and role
  const handleAccountClick = (e) => {
    e.preventDefault();
    
    // Check auth from localStorage directly to avoid AuthProvider dependency
    const token = localStorage.getItem('adminToken');
    const storedUser = localStorage.getItem('admin_user');
    
    if (token && storedUser) {
      try {
        const user = JSON.parse(storedUser);
        // User is logged in
        if (user.role === 'client') {
          // Client goes to their orders page
          navigate('/my-orders');
        } else {
          // Admin/Employee goes to dashboard
          navigate('/admin/dashboard');
        }
      } catch (error) {
        // Error parsing user, go to login
        navigate('/admin/login');
      }
    } else {
      // Not authenticated - go to login page
      navigate('/admin/login');
    }
  };

  // Get cached settings from localStorage immediately
  const getInitialSettings = () => {
    try {
      const cached = localStorage.getItem('siteSettings');
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (e) {
      console.error('Error reading cache:', e);
    }
    return null;
  };

  // Settings state for logo and phone - initialize from cache immediately
  const initialSettings = getInitialSettings();
  const [settings, setSettings] = useState(initialSettings);
  const [logoLoaded, setLogoLoaded] = useState(false);
  
  // Pre-compute logo URLs from initial settings to avoid flicker
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const STORAGE_BASE_URL = API_BASE_URL.replace('/api', '');
  
  const buildLogoUrl = (logo) => {
    if (!logo) return null;
    if (logo.startsWith('http')) return logo;
    if (logo.startsWith('assets/')) return logo;
    if (logo.startsWith('/storage/')) return `${STORAGE_BASE_URL}${logo}`;
    return `${STORAGE_BASE_URL}/storage/${logo}`;
  };
  
  // Pre-load logo image
  useEffect(() => {
    const logoPath = settings?.header_logo;
    if (logoPath) {
      const img = new Image();
      img.onload = () => setLogoLoaded(true);
      img.onerror = () => setLogoLoaded(true);
      img.src = buildLogoUrl(logoPath);
    }
  }, [settings?.header_logo]);

  useEffect(() => {
    // Fetch settings
    const fetchSettings = async () => {
      try {
        const response = await publicApi.getSettings();
        if (response.success) {
          setSettings(response.data);
          // Cache settings in localStorage
          localStorage.setItem('siteSettings', JSON.stringify(response.data));
        }
      } catch (error) {
        console.error('Error fetching header settings:', error);
      }
    };
    fetchSettings();
  }, []);

  // Get logo and phone from settings - NEVER use default logo
  const headerLogo = settings?.header_logo || null;
  const headerLogoBlack = settings?.header_logo_black || null;
  const headerPhone = settings?.header_phone1 || '06 61 70 00 52';
  
  // Get logo URL - return null if no logo
  const getLogoUrl = (logo) => {
    if (!logo) return null;
    return buildLogoUrl(logo);
  };

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const updateHeader = () => {
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      const shouldBeFixed = scrollY > SCROLL_THRESHOLD;
      
      if (shouldBeFixed) {
        header.classList.add("fixed-header");
      } else {
        header.classList.remove("fixed-header");
      }
      
      tickingRef.current = false;
    };

    const handleScroll = () => {
      if (!tickingRef.current) {
        window.requestAnimationFrame(updateHeader);
        tickingRef.current = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    
    // Initial check
    updateHeader();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Set the default language
  const [selectedLanguage, setSelectedLanguage] = useState("Eng");
  const handleLanguageChange = (language) => {
    setSelectedLanguage(language);
  };

  // Set the default currency
  const [selectedCurrency, setSelectedCurrency] = useState("USD");
  const handleCurrencyChange = (currency) => {
    setSelectedCurrency(currency);
  };

  // Use location context for city and pricing
  const { currentCity, loading: cityLoading, cities, changeCity, isCasaRegion } = useLocationContext();
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);

  // Handle city change
  const handleCityChange = (cityId) => {
    changeCity(cityId);
    setLocationDropdownOpen(false);
  };

  // Mobile menu support
  const [menuActive, setMenuActive] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const handleMenuClick = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };
  const handleMenuToggle = () => {
    setMenuActive(!menuActive);
  };

  // Search control support
  const [activeSearch, setActiveSearch] = useState(false);
  const [headerSearchQuery, setHeaderSearchQuery] = useState('');
  const handleSearchToggle = () => {
    setActiveSearch(!activeSearch);
  };

  const goToShop = ({ categoryId = 'all', search = '' } = {}) => {
    const params = new URLSearchParams();
    if (categoryId && categoryId !== 'all') {
      params.set('category', String(categoryId));
    }
    const trimmedSearch = String(search || '').trim();
    if (trimmedSearch) {
      params.set('search', trimmedSearch);
    }
    const queryString = params.toString();
    navigate(queryString ? `/shop?${queryString}` : '/shop');
    setActiveSearch(false);
  };

  const handleHeaderSearchSubmit = (e) => {
    e.preventDefault();
    goToShop({ search: headerSearchQuery });
  };

  const handleHeaderCategoryChange = (e) => {
    const categoryId = e.target.value;
    goToShop({ categoryId });
  };

  // category control support
  const [activeCategory, setActiveCategory] = useState(false);
  const handleCategoryToggle = () => {
    setActiveCategory(!activeCategory);
  };
  const [activeIndexCat, setActiveIndexCat] = useState(null);
  const handleCatClick = (index) => {
    setActiveIndexCat(activeIndexCat === index ? null : index);
  };

  // Dynamic categories from API
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const { priceRegion } = useLocationContext();

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await publicApi.getCategories(priceRegion);
        if (response.success && response.data.length > 0) {
          // Map API data to include icon based on category name
          const categoriesWithIcons = response.data.map(cat => ({
            ...cat,
            icon: getCategoryIcon(cat.name)
          }));
          setCategories(categoriesWithIcons);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, [priceRegion]);

  // Get icon based on category name
  const getCategoryIcon = (name) => {
    const iconMap = {
      'khalie': 'ph-cookie',
      'gâteaux traditionnels': 'ph-cake',
      'prestige': 'ph-crown',
      'les salés': 'ph-pizza',
      'petits salés': 'ph-hamburger',
      'les amandes': 'ph-nut',
      'petits sucrés': 'ph-cookie',
      'ramadanesque': 'ph-moon-stars',
      'traiteur surgelé': 'ph-snowflake',
      'boulangerie': 'ph-bread',
      'nougat et chocolat': 'ph-heart',
      'autres': 'ph-dots-three',
      'huiles': 'ph-drop',
      'amlou': 'ph-jar',
      'les eaux': 'ph-wine',
      'miels': 'ph-drop-half',
      'épicerie': 'ph-shopping-bag',
      'artisanal': 'ph-hand-heart',
    };
    return iconMap[name.toLowerCase()] || 'ph-package';
  };

  // Get display label based on category name (accent/case fixes)
  const normalizeLabelKey = (value) => {
    const text = String(value ?? '').trim().toLowerCase();
    // Remove diacritics to match keys reliably (e.g. "Crèmerie" -> "cremerie")
    return text.normalize('NFD').replace(/\p{Diacritic}/gu, '');
  };

  const getCategoryLabel = (name) => {
    const key = normalizeLabelKey(name);

    const labelMap = {
      // Requested labels
      cremerie: 'CRÈMERIE',
      epicerie: 'ÉPICERIE',
      'gateaux mini sale': 'GÂTEAUX MINI SALÉS',
      'gateaux mini sucre': 'GÂTEAUX MINI SUCRÉS',
      'gateaux sable': 'GÂTEAUX SABLÉ',
      'les eaux': 'EAUX FLORALES',
      'nougat et chocolat': 'NOUGAT ET CHOCOLAT',
      'gateaux traditionnels': 'GÂTEAUX TRADITIONNELS',
    };

    return labelMap[key] || name;
  };

  return (
    <>
      <style>
        {`
          .search-form__input::placeholder {
            color: white !important;
            opacity: 0.9;
          }
          .search-category .select2-container--default .select2-selection--single .select2-selection__rendered {
            color: white !important;
          }
          .search-category .select2-container--default .select2-selection--single {
            background-color: transparent !important;
            border-color: white !important;
          }
          .search-category .select2-container--default .select2-selection--single .select2-selection__arrow {
            color: white !important;
          }
          .search-category .select2-container--default .select2-selection--single .select2-selection__arrow b {
            border-color: white transparent transparent transparent !important;
            border-top-color: white !important;
          }
          .search-category .select2-container--default .select2-selection--single .select2-selection__arrow b::before {
            color: white !important;
            border-color: white !important;
          }
          .search-category .select2-container--default.select2-container--open .select2-selection--single .select2-selection__arrow b {
            border-color: transparent transparent white transparent !important;
            border-bottom-color: white !important;
          }
          .search-category .select2-container--default.select2-container--open .select2-selection--single .select2-selection__arrow b::before {
            color: white !important;
            border-color: white !important;
          }

          @keyframes cartBump {
            0% { transform: translateZ(0) scale(1); }
            22% { transform: translateZ(0) scale(1.18); }
            44% { transform: translateZ(0) scale(0.95); }
            66% { transform: translateZ(0) scale(1.08); }
            82% { transform: translateZ(0) scale(0.995); }
            100% { transform: translateZ(0) scale(1); }
          }

          .cart-bump {
            animation: cartBump 900ms cubic-bezier(.2,.9,.2,1);
            will-change: transform;
          }

          @keyframes headerMiddlePeek {
            0% { transform: translateY(-120%); opacity: 0; }
            28% { transform: translateY(0); opacity: 1; }
            84% { transform: translateY(0); opacity: 1; }
            100% { transform: translateY(-120%); opacity: 0; }
          }

          .header-middle.header-middle-peek {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            width: 100%;
            z-index: 10000;
            animation: headerMiddlePeek 2000ms cubic-bezier(.2,.9,.2,1);
            will-change: transform, opacity;
          }
          .search-category .js-example-basic-single option {
            color: black !important;
          }
          
          /* Location Dropdown Styles */
          .location-box {
            position: relative;
          }
          .location-dropdown {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            margin-top: 8px;
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            box-shadow: 0 10px 40px rgba(0,0,0,0.15);
            max-height: 300px;
            overflow-y: auto;
            z-index: 1000;
            min-width: 180px;
          }
          .location-dropdown::-webkit-scrollbar {
            width: 6px;
          }
          .location-dropdown::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
          }
          .location-dropdown::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 3px;
          }
          .location-dropdown-item {
            padding: 10px 16px;
            font-size: 14px;
            cursor: pointer;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
          }
          .location-dropdown-item:hover {
            background-color: #f3f4f6;
          }
          .location-dropdown-item.active {
            background-color: #fef3c7;
            color: #b8860b;
            font-weight: 600;
          }
          .ph-spin {
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          /* Logo Size Optimization */
          .header-middle {
            padding: 20px 0 !important;
            overflow: visible !important;
          }
          .header-middle .logo {
            display: flex;
            align-items: center;
            height: 100%;
          }
          .header-middle .logo img {
            width: 420px !important;
            height: 100px !important;
            object-fit: contain !important;
            image-rendering: -webkit-optimize-contrast;
            image-rendering: crisp-edges;
            image-rendering: high-quality;
            max-height: 100%;
          }
          @media (max-width: 768px) {
            .header-middle .logo img {
              width: 200px !important;
              height: auto !important;
            }
          }

          /* Category dropdown item alignment (fix count parentheses spacing) */
          .category-dropdown-name {
            line-height: 1.15;
          }
          .category-dropdown-count {
            line-height: 1;
            white-space: nowrap;
            flex-shrink: 0;
          }
        `}
      </style>
      <div className='overlay' />
      <div
        className={`side-overlay ${(menuActive || activeCategory) && "show"}`}
      />
      {/* ==================== Search Box Start Here ==================== */}
      <form onSubmit={handleHeaderSearchSubmit} className={`search-box ${activeSearch && "active"}`}>
        <button
          onClick={handleSearchToggle}
          type='button'
          className='search-box__close position-absolute inset-block-start-0 inset-inline-end-0 m-16 w-48 h-48 border border-gray-100 rounded-circle flex-center text-white hover-text-gray-800 hover-bg-white text-2xl transition-1'
        >
          <i className='ph ph-x' />
        </button>
        <div className='container'>
          <div className='position-relative'>
            <input
              type='text'
              value={headerSearchQuery}
              onChange={(e) => setHeaderSearchQuery(e.target.value)}
              className='form-control py-16 px-24 text-xl rounded-pill pe-64'
              placeholder='Rechercher un produit ou une marque'
            />
            <button
              type='submit'
              className='w-48 h-48 rounded-circle flex-center text-xl position-absolute top-50 translate-middle-y inset-inline-end-0 me-8'
              style={{ backgroundColor: 'black', color: 'white' }}
            >
              <i className='ph ph-magnifying-glass' style={{ color: 'white' }} />
            </button>
          </div>
        </div>
      </form>
      {/* ==================== Search Box End Here ==================== */}
      {/* ==================== Mobile Menu Start Here ==================== */}
      <div
        className={`mobile-menu scroll-sm d-lg-none d-block ${
          menuActive && "active"
        }`}
      >
        <button
          onClick={() => {
            handleMenuToggle();
            setActiveIndex(null);
          }}
          type='button'
          className='close-button'
        >
          <i className='ph ph-x' />{" "}
        </button>
        <div className='mobile-menu__inner'>
          <Link to='/' className='mobile-menu__logo'>
            {getLogoUrl(headerLogoBlack) && <img src={getLogoUrl(headerLogoBlack)} alt='Logo' />}
          </Link>
          <div className='mobile-menu__menu'>
            {/* Nav Menu Start */}
            <ul className='nav-menu flex-align nav-menu--mobile'>
              {/* Home Menu */}
              <li className='nav-menu__item'>
                <Link to='/' className='nav-menu__link' onClick={() => setActiveIndex(null)}>
                  Accueil
                    </Link>
              </li>

              {/* Shop Menu */}
              <li className='nav-menu__item'>
                <Link to='/shop' className='nav-menu__link' onClick={() => setActiveIndex(null)}>
                  Nos Produits
                    </Link>
                  </li>

              {/* Délices Benkirane Menu */}
              <li className='nav-menu__item'>
                <Link to='/index-two' className='nav-menu__link' onClick={() => setActiveIndex(null)}>
                  Délices Benkirane
                    </Link>
              </li>

              {/* Blog Menu */}
              <li className='nav-menu__item'>
                <Link to='/blog' className='nav-menu__link' onClick={() => setActiveIndex(null)}>
                  Actualité & Blog
                    </Link>
              </li>

              {/* Track Order Menu */}
              <li className='nav-menu__item'>
                <Link to='/track-order' className='nav-menu__link' onClick={() => setActiveIndex(null)}>
                  Suivre ma commande
                    </Link>
              </li>

              {/* Contact Us Menu */}
              <li className='nav-menu__item'>
                <Link
                  to='/contact'
                  className='nav-menu__link'
                  onClick={() => setActiveIndex(null)}
                >
                  Nous Contacter
                </Link>
              </li>
            </ul>
            {/* Nav Menu End */}
          </div>
        </div>
      </div>
      {/* ==================== Mobile Menu End Here ==================== */}
      {/* ======================= Middle Top Start ========================= */}
      {/* Top bar commented out - can be uncommented later if needed */}
      {/* 
      <div className='header-top bg-main-600 flex-between'>
        <div className='container container-lg'>
          <div className='flex-between flex-wrap gap-8'>
            <ul className='flex-align flex-wrap d-none d-md-flex'>
              <li className='border-right-item'>
                <Link
                  to='#'
                  className='text-white text-sm hover-text-decoration-underline'
                >
                  Become A Seller
                </Link>
              </li>
              <li className='border-right-item'>
                <Link
                  to='#'
                  className='text-white text-sm hover-text-decoration-underline'
                >
                  About us
                </Link>
              </li>
              <li className='border-right-item'>
                <Link
                  to='#'
                  className='text-white text-sm hover-text-decoration-underline'
                >
                  Free Delivery
                </Link>
              </li>
              <li className='border-right-item'>
                <Link
                  to='#'
                  className='text-white text-sm hover-text-decoration-underline'
                >
                  Returns Policy
                </Link>
              </li>
            </ul>
            <ul className='header-top__right flex-align flex-wrap'>
              <li className='on-hover-item border-right-item border-right-item-sm-space has-submenu arrow-white'>
                <Link to='#' className='text-white text-sm py-8'>
                  Help Center
                </Link>
                <ul className='on-hover-dropdown common-dropdown common-dropdown--sm max-h-200 scroll-sm px-0 py-8'>
                  <li className='nav-submenu__item'>
                    <Link
                      to='#'
                      className='nav-submenu__link hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                    >
                      <span className='text-sm d-flex'>
                        <i className='ph ph-headset' />
                      </span>
                      Call Center
                    </Link>
                  </li>
                  <li className='nav-submenu__item'>
                    <Link
                      to='#'
                      className='nav-submenu__link hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                    >
                      <span className='text-sm d-flex'>
                        <i className='ph ph-chat-circle-dots' />
                      </span>
                      Live Chat
                    </Link>
                  </li>
                </ul>
              </li>
              <li className='on-hover-item border-right-item border-right-item-sm-space has-submenu arrow-white'>
                <Link to='#' className='selected-text text-white text-sm py-8'>
                  {selectedLanguage}
                </Link>
                <ul className='selectable-text-list on-hover-dropdown common-dropdown common-dropdown--sm max-h-200 scroll-sm px-0 py-8'>
                  <li>
                    <Link
                      to='#'
                      className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                      onClick={() => handleLanguageChange("English")}
                    >
                      <img
                        src='assets/images/thumbs/flag1.png'
                        alt=''
                        className='w-16 h-12 rounded-4 border border-gray-100'
                      />
                      English
                    </Link>
                  </li>
                  <li>
                    <Link
                      to='#'
                      className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                      onClick={() => handleLanguageChange("Japan")}
                    >
                      <img
                        src='assets/images/thumbs/flag2.png'
                        alt=''
                        className='w-16 h-12 rounded-4 border border-gray-100'
                      />
                      Japan
                    </Link>
                  </li>
                  <li>
                    <Link
                      to='#'
                      className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                      onClick={() => handleLanguageChange("French")}
                    >
                      <img
                        src='assets/images/thumbs/flag3.png'
                        alt=''
                        className='w-16 h-12 rounded-4 border border-gray-100'
                      />
                      French
                    </Link>
                  </li>
                  <li>
                    <Link
                      to='#'
                      className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                      onClick={() => handleLanguageChange("Germany")}
                    >
                      <img
                        src='assets/images/thumbs/flag4.png'
                        alt=''
                        className='w-16 h-12 rounded-4 border border-gray-100'
                      />
                      Germany
                    </Link>
                  </li>
                  <li>
                    <Link
                      to='#'
                      className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                      onClick={() => handleLanguageChange("Bangladesh")}
                    >
                      <img
                        src='assets/images/thumbs/flag6.png'
                        alt=''
                        className='w-16 h-12 rounded-4 border border-gray-100'
                      />
                      Bangladesh
                    </Link>
                  </li>
                  <li>
                    <Link
                      to='#'
                      className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                      onClick={() => handleLanguageChange("South Korea")}
                    >
                      <img
                        src='assets/images/thumbs/flag5.png'
                        alt=''
                        className='w-16 h-12 rounded-4 border border-gray-100'
                      />
                      South Korea
                    </Link>
                  </li>
                </ul>
              </li>
              <li className='on-hover-item border-right-item border-right-item-sm-space has-submenu arrow-white'>
                <Link to='#' className='selected-text text-white text-sm py-8'>
                  {selectedCurrency}
                </Link>
                <ul className='selectable-text-list on-hover-dropdown common-dropdown common-dropdown--sm max-h-200 scroll-sm px-0 py-8'>
                  <li>
                    <Link
                      to='#'
                      className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                      onClick={() => handleCurrencyChange("USD")}
                    >
                      <img
                        src='assets/images/thumbs/flag1.png'
                        alt=''
                        className='w-16 h-12 rounded-4 border border-gray-100'
                      />
                      USD
                    </Link>
                  </li>
                  <li>
                    <Link
                      to='#'
                      className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                      onClick={() => handleCurrencyChange("Yen")}
                    >
                      <img
                        src='assets/images/thumbs/flag2.png'
                        alt=''
                        className='w-16 h-12 rounded-4 border border-gray-100'
                      />
                      Yen
                    </Link>
                  </li>
                  <li>
                    <Link
                      to='#'
                      className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                      onClick={() => handleCurrencyChange("Franc")}
                    >
                      <img
                        src='assets/images/thumbs/flag3.png'
                        alt=''
                        className='w-16 h-12 rounded-4 border border-gray-100'
                      />
                      Franc
                    </Link>
                  </li>
                  <li>
                    <Link
                      to='#'
                      className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                      onClick={() => handleCurrencyChange("EURO")}
                    >
                      <img
                        src='assets/images/thumbs/flag4.png'
                        alt=''
                        className='w-16 h-12 rounded-4 border border-gray-100'
                      />
                      EURO
                    </Link>
                  </li>
                  <li>
                    <Link
                      to='#'
                      className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                      onClick={() => handleCurrencyChange("BDT")}
                    >
                      <img
                        src='assets/images/thumbs/flag6.png'
                        alt=''
                        className='w-16 h-12 rounded-4 border border-gray-100'
                      />
                      BDT
                    </Link>
                  </li>
                  <li>
                    <Link
                      to='#'
                      className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                      onClick={() => handleCurrencyChange("WON")}
                    >
                      <img
                        src='assets/images/thumbs/flag5.png'
                        alt=''
                        className='w-16 h-12 rounded-4 border border-gray-100'
                      />
                      WON
                    </Link>
                  </li>
                </ul>
              </li>
              <li className='border-right-item'>
                <Link
                  to='/account'
                  className='text-white text-sm py-8 flex-align gap-6'
                >
                  <span className='icon text-md d-flex'>
                    {" "}
                    <i className='ph ph-user-circle' />{" "}
                  </span>
                  <span className='hover-text-decoration-underline'>
                    My Account
                  </span>
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
      */}
      {/* ======================= Middle Top End ========================= */}
      {/* ======================= Middle Header Start ========================= */}
      <header id="header-middle" ref={middleHeaderRef} className='header-middle border-bottom border-gray-100' style={{ backgroundColor: 'black' }}>
        <div className='container container-lg'>
          <nav className='header-inner flex-between'>
            {/* Logo Start */}
            <div className='logo'>
              <Link to='/' className='link'>
                {getLogoUrl(headerLogo) && <img src={getLogoUrl(headerLogo)} alt='Logo' />}
              </Link>
            </div>
            {/* Logo End  */}
            {/* form location Start */}
            <form
              onSubmit={handleHeaderSearchSubmit}
              className='flex-align flex-wrap form-location-wrapper'
            >
              <div className='search-category d-flex h-48 select-border-end-0 radius-end-0 search-form d-sm-flex d-none'>
                <select
                  defaultValue='all'
                  onChange={handleHeaderCategoryChange}
                  className='border border-gray-200 border-end-0 search-category-select'
                  name='category'
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                    color: 'white',
                    minWidth: '180px',
                    padding: '0 12px',
                    borderColor: 'white',
                  }}
                >
                  <option value='all'>Toutes les Catégories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {getCategoryLabel(category.name)}
                    </option>
                  ))}
                </select>
                <div className='search-form__wrapper position-relative'>
                  <input
                    type='text'
                    value={headerSearchQuery}
                    onChange={(e) => setHeaderSearchQuery(e.target.value)}
                    className='search-form__input common-input py-13 ps-16 pe-18 rounded-end-pill pe-44'
                    placeholder='Rechercher un produit ou une marque'
                    style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)', color: 'white' }}
                  />
                  <button
                    type='submit'
                    className='w-32 h-32 rounded-circle flex-center text-xl position-absolute top-50 translate-middle-y inset-inline-end-0 me-8'
                    style={{ backgroundColor: 'black', color: 'white' }}
                  >
                    <i className='ph ph-magnifying-glass' style={{ color: 'white' }} />
                  </button>
                </div>
              </div>
              <div className='location-box bg-white flex-align gap-8 py-6 px-16 rounded-pill border border-gray-100 position-relative'>
                <span className='text-xl d-xs-flex d-none' style={{ color: 'black' }}>
                  <i className='ph ph-map-pin' style={{ color: 'black' }} />
                </span>
                <div className='line-height-1'>
                  <span className='text-xs' style={{ color: 'black' }}>Votre Localisation</span>
                  <div 
                    className='line-height-1 d-flex align-items-center gap-4 cursor-pointer'
                    onClick={() => setLocationDropdownOpen(!locationDropdownOpen)}
                    style={{ cursor: 'pointer' }}
                  >
                    {cityLoading ? (
                      <span className='text-sm fw-medium' style={{ color: 'black' }}>
                        <i className='ph ph-circle-notch ph-spin me-4' />
                        Détection...
                      </span>
                    ) : (
                      <>
                        <span className='text-sm fw-semibold' style={{ color: 'black' }}>
                          {currentCity?.name || 'Casablanca'}
                        </span>
                        <i className={`ph ${locationDropdownOpen ? 'ph-caret-up' : 'ph-caret-down'} text-xs`} style={{ color: 'black' }} />
                      </>
                    )}
                  </div>
                </div>
                {/* Location Dropdown */}
                {locationDropdownOpen && (
                  <div 
                    className='position-absolute bg-white border border-gray-100 rounded-8 shadow-lg py-8 z-5'
                    style={{ 
                      top: '100%', 
                      left: 0, 
                      right: 0, 
                      marginTop: '8px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      minWidth: '180px',
                      zIndex: 1000
                    }}
                  >
                    {cities.map((city) => (
                      <div
                        key={city.id}
                        className={`px-16 py-8 text-sm cursor-pointer hover-bg-gray-100 ${currentCity?.id === city.id ? 'bg-main-50 text-main-600 fw-semibold' : 'text-gray-600'}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleCityChange(city.id)}
                      >
                        {currentCity?.id === city.id && (
                          <i className='ph ph-check me-8 text-main-600' />
                        )}
                        {city.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </form>
            {/* form location start */}
            {/* Header Middle Right start */}
            <div className='header-right flex-align d-lg-block d-none'>
              <div className='flex-align flex-wrap gap-12'>
                <button
                  onClick={handleSearchToggle}
                  type='button'
                  className='search-icon flex-align d-lg-none d-flex gap-4 item-hover'
                >
                  <span className='text-2xl d-flex position-relative item-hover__text' style={{ backgroundColor: 'black', padding: '8px', borderRadius: '50%', color: 'white' }}>
                    <i className='ph ph-magnifying-glass' style={{ color: 'white' }} />
                  </span>
                </button>
                <button onClick={handleAccountClick} className='flex-align gap-4 item-hover' style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <span className='text-2xl d-flex position-relative me-6 mt-6 item-hover__text' style={{ backgroundColor: 'black', padding: '8px', borderRadius: '50%', color: 'white' }}>
                    <i className='ph ph-user' style={{ color: 'white' }} />
                  </span>
                  <span className='text-md item-hover__text d-none d-lg-flex' style={{ color: 'white' }}>
                    Mon Compte
                  </span>
                </button>
                <Link to='/cart' className='flex-align gap-4 item-hover' aria-label="Panier">
                  <span ref={cartIconRef} id="header-cart-icon-desktop" className='text-2xl d-flex position-relative me-6 mt-6 item-hover__text' style={{ backgroundColor: 'black', padding: '8px', borderRadius: '50%', color: 'white' }}>
                    <i className='ph ph-shopping-cart-simple' style={{ color: 'white' }} />
                    <span className='w-16 h-16 flex-center rounded-circle bg-main-600 text-white text-xs position-absolute top-n6 end-n4'>
                      {getCartItemsCount()}
                    </span>
                  </span>
                  <span className='text-md item-hover__text d-none d-lg-flex' style={{ color: 'white' }}>
                    Panier
                  </span>
                </Link>
              </div>
            </div>
            {/* Header Middle Right End  */}
          </nav>
        </div>
      </header>
      {/* ======================= Middle Header End ========================= */}
      {/* ==================== Header Start Here ==================== */}
      <header
        ref={headerRef}
        className="header bg-white border-bottom border-gray-100"
      >
        <div className='container container-lg'>
          <nav className='header-inner d-flex justify-content-between gap-8'>
            <div className='flex-align menu-category-wrapper'>
              {/* Category Dropdown Start */}
              <div className='category on-hover-item'>
                <button
                  onClick={handleCategoryToggle}
                  type='button'
                  className='category__button flex-align gap-8 fw-medium p-16 border-end border-start border-gray-100 text-heading'
                >
                  <span className='icon text-2xl d-xs-flex d-none'>
                    <i className='ph ph-dots-nine' />
                  </span>
                  <span className='d-sm-flex d-none'>Toutes les</span> Catégories
                  <span className='arrow-icon text-xl d-flex'>
                    <i className='ph ph-caret-down' />
                  </span>
                </button>
                <div
                  className={`responsive-dropdown cat on-hover-dropdown common-dropdown nav-submenu p-0 submenus-submenu-wrapper ${
                    activeCategory && "active"
                  }`}
                >
                  <button
                    onClick={() => {
                      handleCategoryToggle();
                      setActiveIndexCat(null);
                    }}
                    type='button'
                    className='close-responsive-dropdown rounded-circle text-xl position-absolute inset-inline-end-0 inset-block-start-0 mt-4 me-8 d-lg-none d-flex'
                  >
                    {" "}
                    <i className='ph ph-x' />{" "}
                  </button>
                  {/* Logo Start */}
                  <div className='logo px-16 d-lg-none d-block'>
                    <Link to='/' className='link'>
                      {getLogoUrl(headerLogo) && <img src={getLogoUrl(headerLogo)} alt='Logo' />}
                    </Link>
                  </div>
                  {/* Logo End */}
                  <ul className='scroll-sm p-0 py-8' style={{ width: '265px', maxHeight: '400px', overflowY: 'auto' }}>
                    {categoriesLoading ? (
                      // Loading skeleton
                      [...Array(5)].map((_, i) => (
                        <li key={i} className='py-12 px-16'>
                          <div className='bg-gray-100 rounded' style={{ height: 20, width: '80%' }}></div>
                        </li>
                      ))
                    ) : (
                      categories.map((category, index) => (
                        <li 
                          key={category.id} 
                          onClick={() => handleCatClick(index)}
                          className={`has-submenus-submenu ${activeIndexCat === index ? "active" : ""}`}
                        >
                          <Link
                            to={`/shop?category=${category.id}`}
                            className='text-gray-500 text-15 py-12 px-16 flex-align gap-8 rounded-0'
                          >
                            <span className='text-xl d-flex'>
                              <i className={`ph ${category.icon}`} />
                            </span>
                            <span className='category-dropdown-name'>{getCategoryLabel(category.name)}</span>
                            <span className='category-dropdown-count text-xs text-gray-400 ms-auto'>
                              ({category.products_count || 0})
                            </span>
                          </Link>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              </div>
              {/* Category Dropdown End  */}
              {/* Menu Start  */}
              <div className='header-menu d-lg-block d-none'>
                {/* Nav Menu Start */}
                <ul className='nav-menu flex-align '>
                  <li className='nav-menu__item'>
                        <NavLink
                          to='/'
                          className={(navData) =>
                            navData.isActive
                          ? "nav-menu__link activePage"
                          : "nav-menu__link"
                          }
                        >
                      Accueil
                        </NavLink>
                      </li>
                  <li className='nav-menu__item'>
                        <NavLink
                          to='/shop'
                          className={(navData) =>
                            navData.isActive
                          ? "nav-menu__link activePage"
                          : "nav-menu__link"
                          }
                        >
                      Nos Produits
                        </NavLink>
                      </li>
                  <li className='nav-menu__item'>
                        <NavLink
                      to='/index-two' 
                          className={(navData) =>
                            navData.isActive
                          ? "nav-menu__link activePage"
                          : "nav-menu__link"
                          }
                        >
                      Délices Benkirane
                        </NavLink>
                      </li>
                  <li className='nav-menu__item'>
                        <NavLink
                      to='/blog' 
                          className={(navData) =>
                            navData.isActive
                          ? "nav-menu__link activePage"
                          : "nav-menu__link"
                          }
                        >
                      Actualité & Blog
                        </NavLink>
                      </li>
                  <li className='nav-menu__item'>
                        <NavLink
                      to='/track-order' 
                          className={(navData) =>
                            navData.isActive
                          ? "nav-menu__link activePage"
                          : "nav-menu__link"
                          }
                        >
                      Suivre ma commande
                        </NavLink>
                      </li>
                  <li className='nav-menu__item'>
                    <NavLink
                      to='/contact'
                      className={(navData) =>
                        navData.isActive
                          ? "nav-menu__link activePage"
                          : "nav-menu__link"
                      }
                    >
                      Nous Contacter
                    </NavLink>
                  </li>
                </ul>
                {/* Nav Menu End */}
              </div>
              {/* Menu End  */}
            </div>
            {/* Header Right start */}
            <div className='header-right flex-align'>
              <a
                href={`tel:${headerPhone.replace(/\s/g, '')}`}
                className='header-phone-btn text-white h-100 flex-align gap-8 text-lg d-lg-flex d-none'
              >
                <div className='d-flex text-32'>
                  <i className='ph ph-phone-call' />
                </div>
                {headerPhone}
              </a>
              <div className='me-16 d-lg-none d-block'>
                <div className='flex-align flex-wrap gap-12'>
                  <button
                    onClick={handleSearchToggle}
                    type='button'
                    className='search-icon flex-align d-lg-none d-flex gap-4 item-hover'
                  >
                    <span className='text-2xl d-flex position-relative item-hover__text' style={{ backgroundColor: 'black', padding: '8px', borderRadius: '50%', color: 'white' }}>
                      <i className='ph ph-magnifying-glass' style={{ color: 'white' }} />
                    </span>
                  </button>
                  <button onClick={handleAccountClick} className='flex-align gap-4 item-hover' style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                    <span className='text-2xl d-flex position-relative me-6 mt-6 item-hover__text' style={{ backgroundColor: 'black', padding: '8px', borderRadius: '50%', color: 'white' }}>
                      <i className='ph ph-user' style={{ color: 'white' }} />
                    </span>
                    <span className='text-md item-hover__text d-none d-lg-flex' style={{ color: 'white' }}>
                      Mon Compte
                    </span>
                  </button>
                  <Link to='/cart' className='flex-align gap-4 item-hover' aria-label="Panier">
                    <span id="header-cart-icon-mobile" className='text-2xl d-flex position-relative me-6 mt-6 item-hover__text' style={{ backgroundColor: 'black', padding: '8px', borderRadius: '50%', color: 'white' }}>
                      <i className='ph ph-shopping-cart-simple' style={{ color: 'white' }} />
                      <span className='w-16 h-16 flex-center rounded-circle bg-main-600 text-white text-xs position-absolute top-n6 end-n4'>
                        {getCartItemsCount()}
                      </span>
                    </span>
                    <span className='text-md item-hover__text d-none d-lg-flex' style={{ color: 'white' }}>
                      Cart
                    </span>
                  </Link>
                </div>
              </div>
              <button
                onClick={handleMenuToggle}
                type='button'
                className='toggle-mobileMenu d-lg-none ms-3n text-gray-800 text-4xl d-flex'
              >
                {" "}
                <i className='ph ph-list' />{" "}
              </button>
            </div>
            {/* Header Right End  */}
          </nav>
        </div>
      </header>
      {/* ==================== Header End Here ==================== */}
    </>
  );
};

export default HeaderOne;
